import { cx } from '@emotion/css';
import type { CSSProperties, FC, PropsWithChildren } from 'react';

import type { BackgroundColor } from '../../constants';
import { BackgroundStyle } from '../../constants/background';
import { m, MotifComponent, useMotifStyles } from '../../motif';
import type { BaseComponentProps, ImageSources } from '../../types';
import { getBackgroundClassName } from '../../utils';
import { Picture } from '../Picture';
import { Video } from '../Video';
import {
  backgroundMediaCss,
  fixedBackgroundMediaCss,
  innerContainerCss,
  outerContainerCss,
  pageBgColorCss,
  pageGutterCss,
  pageTransparentBgCss,
} from './Page.styled';
import { ScrollSnapContainer } from './ScrollSnapContainer';

export interface PageProps extends BaseComponentProps {
  backgroundColor?: BackgroundColor;
  backgroundImageSources?: ImageSources;
  backgroundMediaStyle?: BackgroundStyle; // Not CSS properties
  backgroundPosterSource?: string;
  backgroundVideoSource?: string;
  mobileBackgroundVideoSource?: string;
  scrollSnap?: boolean;
  gutterMatchesPageBackground?: boolean;
}

export const Page: FC<PropsWithChildren<PageProps>> = ({
  backgroundColor: pageBackgroundColor,
  backgroundImageSources,
  backgroundMediaStyle = BackgroundStyle.Normal,
  backgroundPosterSource,
  backgroundVideoSource,
  children,
  className: pageClassName,
  mobileBackgroundVideoSource,
  scrollSnap,
  gutterMatchesPageBackground,
}) => {
  useMotifStyles(MotifComponent.PAGE);

  const innerContainerClassName = cx(
    innerContainerCss,
    getBackgroundClassName(pageBackgroundColor),
    pageGutterCss,
    pageClassName,
    /**
     * Need set bg to transparent if bg img or video because we need to keep z-index: -1 on the
     * background media to ensure it's behind the content because some content does not set
     * position: relative. TODO: This is a temporary solution until we have a better way to handle
     * (specifically, cheerios is broken without this, can potentially be fixed by setting
     * position:relative on CheeriosStaticBlock) https://jira.sc-corp.net/browse/WEBP-9706
     *
     * TLDR: If there is no background media, we can use the page background color, otherwise its
     * transparent.
     */
    backgroundImageSources || backgroundVideoSource ? pageTransparentBgCss : pageBgColorCss
  );

  const gutterColorCss: CSSProperties | undefined = gutterMatchesPageBackground
    ? {
        // @ts-ignore using a css variable
        '--gutter-color': m('--bg-color'), // Use the page background color as gutter color
      }
    : undefined;

  /**
   * Split `container-*` styles out to a separate wrapper to resolve issues with margins when
   * 'container-type' is applied, e.g. Snap.com home page
   */
  return (
    <div data-testid={MotifComponent.PAGE} className={cx(outerContainerCss, MotifComponent.PAGE)}>
      <div className={innerContainerClassName} style={gutterColorCss}>
        {backgroundImageSources && (
          <Picture
            imgSrcs={backgroundImageSources}
            imgClassName={
              backgroundMediaStyle === BackgroundStyle.Fixed
                ? fixedBackgroundMediaCss
                : backgroundMediaCss
            }
            fetchPriority="auto"
          />
        )}
        {backgroundVideoSource ? (
          <Video
            className={
              backgroundMediaStyle === BackgroundStyle.Fixed
                ? fixedBackgroundMediaCss
                : backgroundMediaCss
            }
            source={backgroundVideoSource}
            mobileSource={mobileBackgroundVideoSource}
            posterSource={backgroundPosterSource}
            isBackgroundVideo
          />
        ) : undefined}
        {scrollSnap ? <ScrollSnapContainer>{children}</ScrollSnapContainer> : children}
      </div>
    </div>
  );
};

Page.displayName = 'Page';
